import React from 'react';
import { Box, Grid, IconButton, Paper, Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';
import { DragIndicator, Edit } from '@mui/icons-material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import { AccountData, CatalogCategoryOut, Language } from '../api';
import { WorkshopCircle } from '../common/ui/WorkshopCircle';
import { localizedLabel } from '../utils/maskUtils';

type SortableCategoryRowProps = {
  category: CatalogCategoryOut;
  account: AccountData;
  language: Language;
  readOnly: boolean;
  onEdit: () => void;
};

export const SortableCategoryRow = ({ category, account, language, readOnly, onEdit }: SortableCategoryRowProps) => {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: category.id,
    disabled: readOnly,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
        <Grid container alignItems="center" flexWrap="nowrap" gap={1}>
          {!readOnly && (
            <Box
              {...listeners}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'grab', '&:active': { cursor: 'grabbing' } }}>
              <DragIndicator sx={{ color: 'text.secondary' }} />
            </Box>
          )}
          <Grid item flex={1} minWidth={0} container flexDirection="column">
            <Typography fontWeight={600} noWrap>
              {localizedLabel(category.label, language)}
            </Typography>
            {category.order_page_ids?.length ? (
              <Grid container gap={0.5} alignItems="center">
                {category.order_page_ids.map((orderPageId) => (
                  <WorkshopCircle
                    key={orderPageId}
                    account={account}
                    orderPageId={orderPageId}
                    language={language}
                    withTooltip
                  />
                ))}
              </Grid>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {t('catalog.allPages')}
              </Typography>
            )}
          </Grid>
          <Tooltip title={readOnly ? t('noPermissions') : t('edit')}>
            <span>
              <IconButton disabled={readOnly} onClick={onEdit}>
                <Edit />
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
      </Paper>
    </div>
  );
};
